// The Help Guide text (H3): edit wording here only. Button labels are marked with square brackets.

export interface GuideSection {
  title: string;
  intro?: string;
  steps: string[];
}

export const GUIDE_SECTIONS: GuideSection[] = [
  {
    title:
      `Start here`,
    intro:
      `This app records what is spent on the houses: each payment, what it was for and which house it was for. Complete records make tax time simple. The app organizes the records; it is not tax advice.`,
    steps: [
      `Open the app. Type your email address in the "Email address" box.`,
      `If this phone is set up for it, tap [Sign in with this phone]. Your phone asks you to confirm it's you. Do what it shows, and the app opens.`,
      `Otherwise, tap [Email me a sign-in code]. A 6-digit code comes to your email. It works for 10 minutes.`,
      `Type the code in the "Sign-in code" box and tap [Sign in with code]. On an iPhone, the code may appear above the keyboard: tap it to fill it in.`,
      `If the app says "That code didn't work. Check it, or send a new code.", check the numbers, or tap [Send a new code] and use the newest code.`,
      `To sign in this way next time: on the Home screen, tap [Help / Guide], then [Set up sign-in on this device], and do what your phone shows.`,
      `If this device already signs you in this way and your phone offers to save a new passkey, tap Cancel.`,
      `The app keeps you signed in. It never logs you out by itself.`,
    ],
  },
  {
    title:
      `The Home screen`,
    intro:
      `The Home screen has five large buttons.`,
    steps: [
      `[Add Spending] records money that was paid: a receipt, a bill, or a Zelle or ACH payment.`,
      `[View Payments] shows "Each payment, and who paid".`,
      `[View Expenses] shows "What each house has cost".`,
      `[Add Income] does nothing yet.`,
      `[Help / Guide] opens this guide. [Set up sign-in on this device] and [Log out] are at the bottom of the guide's first screen.`,
    ],
  },
  {
    title:
      `Payments and expenses`,
    steps: [
      `A payment is one time money was paid to someone: one receipt, one bill, one transfer.`,
      `An expense is one part of a payment: one house, and one kind of cost.`,
      `Example: at a hardware store you pay $60 in all: $30 of paint for one house, $20 of tools for the other house, and $10 of snacks for yourself.`,
      `That is one payment with two expenses: $30 for one house and $20 for the other.`,
      `The $10 for yourself is not an expense. The payment's total counts only the expenses, $50, and a "Note" on the payment says how much of the receipt was "Not for the properties".`,
      `[View Payments] lists the payments. [View Expenses] lists every expense, with the totals for each house.`,
    ],
  },
  {
    title:
      `Add a purchase with a receipt`,
    intro:
      `Enter each purchase soon after paying, while the receipt is at hand and easy to read. Enter it once: one receipt is one payment.`,
    steps: [
      `On the Home screen, tap [Add Spending]. The app asks "Do you have a receipt or a bill?"`,
      `Tap [Take photo] and photograph the whole receipt, flat and in good light.`,
      `Or tap [Choose file] to pick a photo or a PDF already saved on your phone. If the receipt came by email as a PDF, first save it to the Files app, then tap [Choose file] and pick it.`,
      `Wait while the app shows "Reading your receipt…". If it shows "Still reading your receipt…", tap [Keep waiting].`,
      `The app fills in what it read. On each screen, check the answer, fix it if it is wrong, and tap [Next]: "Who was paid?", "When was it paid?" and "How much was paid in total?".`,
      `If you see "Please double-check — we weren't sure about this one.", check every answer with extra care.`,
      `"Where did the money come from?": tap [LLC bank account], or tap [An owner paid personally] and then the owner. The app never chooses this for you.`,
      `"How was it paid?": tap how it was paid, or tap [Skip] if none of them fit.`,
      `"Is everything for one property?": tap the house. If it was for both houses, or partly for yourself, tap [Split it] instead (section 7).`,
      `Answer "What was this for?" (section 8), then "What kind of expense is this?" if the app asks, then the small-purchase question (section 9).`,
      `"Describe it": add a few words about what it was for, such as the room or the job. A clear description makes the record easy to understand later. Then tap [Next].`,
      `The last screen shows everything you entered. If anything is wrong, tap [Back] to fix it. When it is right, tap [Confirm & Save].`,
      `"Saved ✓" means it is recorded. Tap [Add another] for the next one, or [Done].`,
    ],
  },
  {
    title:
      `Add a purchase without a receipt`,
    steps: [
      `Use this only when there is nothing to photograph or attach.`,
      `On the Home screen, tap [Add Spending], then [No document].`,
      `"Who was paid?": type the store, company or person.`,
      `"When was it paid?" starts at today's date. Change it if it was paid on another day.`,
      `"How much was paid in total?": type the amount.`,
      `Answer the rest as in section 4, from "Where did the money come from?" on.`,
    ],
  },
  {
    title:
      `Adding a Zelle or ACH payment`,
    steps: [
      `Keep the confirmation: a screenshot of the confirmation screen, or the emailed receipt. It is the document for this payment.`,
      `On the Home screen, tap [Add Spending], then [Choose file] and pick the screenshot or the saved receipt. Do not tap [No document].`,
      `Check every answer. The app may fill in some of them, or none. Type any that are blank.`,
      `"Where did the money come from?": tap [LLC bank account] if the LLC's account paid, or [An owner paid personally] and then the owner.`,
      `"How was it paid?": tap [ACH] or [Zelle].`,
      `Finish as in section 4.`,
      `Use [No document] only when there is nothing at all to attach.`,
    ],
  },
  {
    title:
      `Splitting between the houses`,
    steps: [
      `On "Is everything for one property?", tap [Split it], then [Next].`,
      `The screen shows the total and "Remaining to assign". You can go on only when it reaches $0.00.`,
      `Choose how to split: [Tap items], [Type amounts] or [Use percentages].`,
      `[Tap items] appears only after [Split it], and only when the app read the receipt clearly and its figures agree. If you change the total, it goes away. When it is not there, use [Type amounts].`,
      `With [Tap items]: under "Receipt items", tap where each item goes: a line, [Not for the properties], or [Split this item].`,
      `With [Type amounts]: type each line's amount. If the receipt has sales tax, type the amounts before tax and the tax under "Sales tax (optional)". The app shares out the tax.`,
      `With [Use percentages]: type each line's percent. The button that starts "50/50 between" fills in half for each house.`,
      `Each line needs its house and what it was for. Tap [Details] on the line, tap the house under "Property", answer "What was this for?", tap a "Category" if the app shows them, answer the small-purchase question, then tap [Done].`,
      `To add a line, tap [+ Add a line]. To take one away, tap [Remove].`,
      `Anything for yourself goes under "Not for the properties". It is not recorded as an expense, so the house totals show only what the houses cost (section 3).`,
      `When "Remaining to assign" shows $0.00, tap [Next]. If [Next] stays grey, the lines under it say why.`,
    ],
  },
  {
    title:
      `"What was this for?"`,
    steps: [
      `Every expense asks "What was this for?". Tap one answer.`,
      `[Improvement]: "Renovating, upgrading, rebuilding".`,
      `[Repair or upkeep]: "Fixing or maintaining what's there".`,
      `[Not building work]: "Fees, taxes, insurance, utilities, interest".`,
      `For anything bought for the renovation, tap [Improvement], even if the app suggested something else.`,
      `While a house is under construction, the app starts with [Improvement] chosen and says "Recorded as an improvement while the house is under construction."`,
      `If you choose another answer for a house under construction, the app asks you to describe it. Type a few words about what it was.`,
      `After [Repair or upkeep] or [Not building work], the app asks "What kind of expense is this?". Tap the one that fits.`,
    ],
  },
  {
    title:
      `The small-purchase question`,
    steps: [
      `Each expense asks "Mark for the small-purchase treatment?", with [Yes — mark it] and [No — leave unmarked].`,
      `The app has already chosen the usual answer for that kind of expense, and says "Using the usual answer for this category."`,
      `Leave it as the app set it.`,
      `The answer records only whether the expense is marked. The amount plays no part in it.`,
      `If you see "You changed this from the usual answer.", tap the other answer to put it back.`,
    ],
  },
  {
    title:
      `Finding a payment`,
    steps: [
      `On the Home screen, tap [View Payments]. The newest payments are at the top, grouped by month.`,
      `Each card shows who was paid, the amount, the date, the house and who paid. Tap a card to open the payment.`,
      `The payment shows its "Expenses in this payment", its "Owner payback" and its "Attachments".`,
      `Tap [Back to payments] at the top to go back to the list. The list keeps your place.`,
      `To find a payment, tap [Filter & sort] at the top. Tap your choices under "Sort", "House", "Who paid", "Year" or "Paid to".`,
      `Tap [Done] to see the list. [Clear all] puts every choice back.`,
      `Tap [Home] at the top to go back to the Home screen. Your choices are cleared when you leave.`,
    ],
  },
  {
    title:
      `Checking totals`,
    steps: [
      `On the Home screen, tap [View Expenses].`,
      `At the top are the number of expenses and their total, then "By house": what each house has cost.`,
      `Below, "Every expense" lists each one, newest first. Tap one to open its payment ("Tap to see the payment").`,
      `An expense marked "Logged from a receipt — not part of a payment" does not open.`,
      `To see one house's total, tap [Filter & sort], tap the house under "House", then tap [Done].`,
      `With any other choice, the screen says "Totals show when only a house is chosen."`,
      `[Clear all] brings back every expense and the full totals.`,
    ],
  },
  {
    title:
      `If something goes wrong`,
    steps: [
      `Saved something by mistake, or saved it wrong? Do not enter it again: a second entry counts the money twice. A saved entry cannot be changed in the app yet, so report what was wrong and it will be corrected.`,
      `"This receipt has already been saved." means it is already recorded. Tap [Home]. There is nothing more to do.`,
      `If you see "We couldn't save this payment. Please try again — nothing was saved.", tap [Back to review], then [Confirm & Save] again.`,
      `If an upload does not go through, tap [Try again]. If it fails again, take a clearer photo.`,
      `If you see "Your session couldn't be confirmed. Please try again.", try again in a moment. The app does not log you out.`,
      `If nothing helps, note what the screen says and report it.`,
      `To log out of this phone, tap [Log out] at the bottom of the guide's first screen, and confirm.`,
    ],
  },
];
